import { ChevronRight } from 'lucide-react'
import { generatePath, Link } from 'react-router-dom'

import { type GetPatientResponse, stripHtmlToText } from '@snootsvet/shared'

import { Copyable } from '@/frontend/components/copyable'
import { Badge } from '@/frontend/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/frontend/components/ui/card'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/frontend/components/ui/hover-card'
import { SpeciesIcon } from '@/frontend/features/patient/species-icon'
import { RegionFlag } from '@/frontend/features/region/region-flag'
import { cn } from '@/frontend/lib/utils'
import { routes } from '@/frontend/routes'

type Owner = GetPatientResponse['patient']['owner']

export function OwnerCard({ owner }: { owner: Owner }) {
  const initials = `${owner.firstName[0]}${owner.lastName[0]}`
  const memberUrl = generatePath(routes.member, { memberId: owner.id })
  const notes = owner.internalNotes ? stripHtmlToText(owner.internalNotes) : null
  const sortedPets = [
    ...owner.pets.filter((pet) => pet.archivedAt === null),
    ...owner.pets.filter((pet) => pet.archivedAt !== null),
  ]
  const visiblePets = sortedPets.slice(0, 2)
  const remainingPets = sortedPets.slice(2)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Owner</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted font-medium">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1.5 font-medium">
              <span className={cn('truncate', owner.archivedAt && 'text-muted-foreground')}>
                {owner.firstName} {owner.lastName}
              </span>
              <RegionFlag region={owner.region} className="shrink-0" />
              {owner.archivedAt && (
                <Badge variant="secondary" size="sm" className="shrink-0">
                  Archived
                </Badge>
              )}
            </p>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Copyable value={owner.email} label="Email" className="min-w-0" />
              <span>·</span>
              <Copyable value={owner.phone} label="Phone" className="min-w-0" />
            </div>
            {owner.pets.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {visiblePets.map((pet) => (
                  <PetBadge key={pet.id} pet={pet} />
                ))}
                {remainingPets.length > 0 && (
                  <HoverCard>
                    <HoverCardTrigger
                      render={
                        <Badge
                          render={
                            <button
                              type="button"
                              aria-label={`View ${remainingPets.length} more pets`}
                            />
                          }
                          variant="outline"
                          size="xs"
                        />
                      }
                    >
                      + {remainingPets.length} more
                    </HoverCardTrigger>
                    <HoverCardContent className="flex w-auto flex-wrap gap-1">
                      {remainingPets.map((pet) => (
                        <PetBadge key={pet.id} pet={pet} />
                      ))}
                    </HoverCardContent>
                  </HoverCard>
                )}
              </div>
            )}
            {notes && <p className="line-clamp-2 italic text-2xs text-muted-foreground">{notes}</p>}
          </div>
          <Link
            to={memberUrl}
            className="flex shrink-0 items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground font-semibold"
          >
            View
            <ChevronRight className="size-4" />
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}

function PetBadge({ pet }: { pet: Owner['pets'][number] }) {
  const patientUrl = generatePath(routes.patient, { patientId: pet.id })
  return (
    <Badge
      render={<Link to={patientUrl} />}
      variant={pet.archivedAt ? 'neutral' : 'outline'}
      size="xs"
    >
      <SpeciesIcon species={pet.species} className="size-2.5" />
      <span className={cn(pet.archivedAt && 'line-through')}>{pet.name}</span>
      {pet.archivedAt && <span className="sr-only"> (archived)</span>}
    </Badge>
  )
}
